// src/pages/Login.jsx
import React, { useState } from "react";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const [search] = useSearchParams();
  const carryClinic = search.get("clinic") || "";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  async function submit(e) {
    e.preventDefault();
    setMsg("");
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);

      // If coming from /login?clinic=CODE, send to /dashboard?clinic=CODE
      const next = carryClinic
        ? `/dashboard?clinic=${encodeURIComponent(carryClinic)}`
        : (loc.state && loc.state.from && loc.state.from.pathname) || "/dashboard";

      nav(next, { replace: true });
    } catch (e) {
      setMsg(`${e?.code || "auth/error"} — ${e?.message || "Login failed"}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/60 via-white to-white">
      <div className="mx-auto max-w-md px-4 py-12">
        <div className="rounded-2xl border border-gray-200 bg-white/90 shadow-sm backdrop-blur">
          <div className="border-b px-6 py-4">
            <h1 className="text-xl font-semibold tracking-tight text-gray-900">Clinic Login</h1>
            {carryClinic && (
              <div className="mt-1 text-xs text-gray-500">
                You’ll connect to clinic <span className="font-mono font-medium">{carryClinic}</span> after login.
              </div>
            )}
          </div>

          <form onSubmit={submit} className="space-y-4 px-6 py-6">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-800">Work email</label>
              <input
                type="email"
                placeholder="name@hospital.org"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-800">Password</label>
              <div className="flex items-stretch gap-2">
                <input
                  type={showPw ? "text" : "password"}
                  placeholder="Your password"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="rounded-lg border border-gray-300 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
              disabled={busy}
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>

            {msg && <div className="rounded-md bg-rose-50 p-2 text-sm text-rose-700">{msg}</div>}

            <div className="flex items-center justify-between pt-2 text-sm">
              <Link className="text-indigo-600 hover:underline" to="/forgot">Forgot password?</Link>
              <Link className="text-indigo-600 hover:underline" to="/signup">Create account</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
